/**
 * iter87.437 (user-requested — "mirror NFL exactly") — CFB Playoffs
 * Dark Horse Pick. ONE pick (not NFL's two, one per conference) — CFB's
 * 12-team field isn't cleanly 2-way groupable like NFL's AFC/NFC (the
 * admin-curated `conference` label on each team varies — SEC, Big Ten,
 * ACC, Group of 5, etc. — not a clean binary split), so this mirrors
 * NWSL's single-slot shape instead. iter87.450 (user-requested):
 * eligibility tightened to seed 7-12 only (seeds 1-6 are out — stricter
 * than the First-Round-bye cutoff of seed 5-12), and the per-win bonus
 * raised to 8 (`tournament.scoring.cfb_darkhorse_win_points`, default
 * 8), stacking across every round that team wins.
 *
 * iter87.467 (user-requested — floating Save button on every
 * predictor/Dark Horse modal) — tapping a team used to save
 * instantly; now it only drafts a selection, and a floating "Save
 * Pick" footer button (always visible) commits it.
 */
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Flag, AlertTriangle, Info, Check } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

export default function CfbDarkhorsePickModal({ open, onClose, onSaved, tournamentId, participantId, existingPick, perWinPoints = 8 }) {
    const [savedPick, setSavedPick] = useState(existingPick || null);
    const [draftPick, setDraftPick] = useState(existingPick || null);
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setSavedPick(existingPick || null);
        setDraftPick(existingPick || null);
        api.getCfbPoBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPick]);

    if (!open) return null;
    const locked = !!bracket?.pre_season_locked;
    const eligible = (bracket?.teams || [])
        .filter((t) => !t.missed_playoffs && Number(t.seed) >= 7)
        .sort((a, b) => a.seed - b.seed);
    const savedTeam = savedPick ? eligible.find((t) => t.id === savedPick) : null;
    const dirty = draftPick !== savedPick;

    const onSavePick = async () => {
        if (!draftPick || locked) return;
        setSaving(true);
        try {
            await api.submitCfbDarkhorsePick(tournamentId, participantId, draftPick);
            setSavedPick(draftPick);
            onSaved?.(draftPick);
            toast.success("Dark Horse pick saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your pick");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="cfb-darkhorse-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-heat/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-heat flex items-center justify-center shrink-0">
                        <Flag className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Dark Horse Pick</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-cfb-darkhorse-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="cfb-darkhorse-pick-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick 1 team (seed 7-12 only — seeds 1-6 are out). +{perWinPoints} pts for every playoff game that team wins, stacking across rounds. Locks the instant the First Round kicks off.
                </p>
            </div>

            {locked && (
                <p data-testid="cfb-darkhorse-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — the First Round has started. Your pick below is final.
                </p>
            )}

            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2" data-testid="cfb-darkhorse-current">
                {savedTeam ? <>Saved: <span className="text-heat">{savedTeam.name}</span></> : "No pick saved yet"}
            </p>

            {eligible.length === 0 ? (
                <div className="text-volt text-[11px] font-semibold flex items-start gap-1.5 bg-volt/10 border border-volt/40 rounded-lg px-3 py-2" data-testid="cfb-darkhorse-no-teams">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                        The College Football Playoff field isn't set yet — the selection committee announces the
                        final 12 teams after the last scheduled game of the regular season. Come back once the
                        bracket is announced to make your pick.
                    </span>
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-2">
                    {eligible.map((t) => {
                        const isSelected = draftPick === t.id;
                        return (
                            <motion.button
                                key={t.id}
                                data-testid={`cfb-darkhorse-option-${t.id}`}
                                disabled={locked}
                                onClick={() => !locked && setDraftPick(t.id)}
                                whileHover={!locked ? { y: -2, scale: 1.015 } : {}}
                                whileTap={!locked ? { scale: 0.975 } : {}}
                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                className={`p-2.5 rounded-lg border text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                                    isSelected
                                        ? "border-heat bg-heat/[0.08]"
                                        : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-heat/40"
                                }`}
                            >
                                <TeamSwatch name={t.name} sport="cfb" size="w-7 h-7 text-[9px]" />
                                <div className="text-xs font-bold truncate mt-1.5 flex items-center gap-1">{t.name}{isSelected && <Check className="w-3 h-3 text-heat shrink-0" />}</div>
                                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5 truncate">
                                    {t.conference ? `${t.conference} · ` : ""}Seed #{t.seed}
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            )}

            </div>

            <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="cfb-darkhorse-pick-footer">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="cfb-darkhorse-pick-count">
                        {dirty ? "Unsaved pick" : savedTeam ? "Saved" : "No pick yet"}
                    </span>
                    <button
                        type="button"
                        disabled={saving || !dirty || locked}
                        onClick={onSavePick}
                        data-testid="cfb-darkhorse-save-btn"
                        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-heat text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Pick"}
                    </button>
                </div>
            </div>
        </BentoModal>
    );
}
